import { CreateTimesheetProposalEntryOnlySchema } from '@timelyapp/integration-cloud-api-spec';
import type { CreateTimesheetProposalEntryApiArg } from '~/redux/modules/timely/api-slice/GENERATED_ARTIFACT';
import { timelyApi } from '~redux/modules/timely/api-slice/timely.apiSlice';
import type { TimesheetEntry } from '../types/types.TimesheetProposals';

// Counter to ensure unique optimistic IDs even when created at the same timestamp
let optimisticCounter = 0;

/**
 * Creates a unique optimistic ID that won't collide with other optimistic entries
 */
function createUniqueOptimisticId(prefix: string = 'optimistic') {
  return `${prefix}_${Date.now()}_${++optimisticCounter}`;
}

/**
 * Build an optimistic entry for immediate cache patching during timesheet
 * proposal entry creation. Items live on `proposal.items[]` and are attributed
 * to entries by timestamp containment — entries don't carry items of their own,
 * so this returns just the entry. Existing items in `proposal.items[]` get
 * re-bucketed as soon as the new entry is in the cache.
 */
export function createOptimisticEntry(args: CreateTimesheetProposalEntryApiArg) {
  const entryId = createUniqueOptimisticId('optimistic');
  const now = new Date().toISOString();
  return {
    ...CreateTimesheetProposalEntryOnlySchema.parse({ ...args.body }),
    id: entryId,
    timesheet_proposal_id: args.proposalId,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  };
}

/**
 * Generic cache update utility for timesheet proposal operations.
 * Handles optimistic updates and rollbacks consistently across all mutations.
 *
 * Targets the Rails `getTimesheetProposal` cache, which `flattenArg` keys by a
 * BARE STRING proposal id (not `{ id }`). So `selectCachedArgsForQuery` yields
 * bare-string args — match them directly against `proposalId` and pass the bare
 * string straight to `updateQueryData`.
 */
export function updateProposalCache<T>(
  dispatch: any,
  cachedQueries: any[],
  proposalId: string,
  updateFn: (draft: any) => void,
  queryFulfilled: Promise<T>
) {
  const patchResults = cachedQueries
    .filter(query => query === proposalId)
    .map(query =>
      dispatch(timelyApi.util.updateQueryData('getTimesheetProposal', query, updateFn))
    );

  queryFulfilled.catch(() => patchResults.forEach(patch => patch.undo()));
  return patchResults;
}

/**
 * Creates an optimistic manual entry for local state management.
 * This entry is stored locally until a project is assigned, preventing
 * database entries with timely_project_id: 0.
 */
export function createOptimisticManualEntry(currentDate: string): TimesheetEntry {
  const manualId = createUniqueOptimisticId('manual');
  return {
    id: manualId,
    day: currentDate,
    projectId: 0, // 0 indicates needs project assignment (local only)
    duration: {
      total_seconds: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      formatted: '0m',
      total_hours: 0,
      total_minutes: 0,
    },
    description: '',
    labelIds: [],
    activities: [],
  };
}
